import { useContext, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Paper,
  Typography,
  Button,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { ReactComponent as SecurityUploadIcon } from "../../../images/SecurityUploadIcon.svg";
import { ReactComponent as DeleteIcon } from "../../../images/SecurityDeleteIcon.svg";
import { ReactComponent as BusinessDocIcon } from "../../../images/BusinessDoc.svg";
import MessagePopup from "../../popup-components/MessagePopup";
import * as XLSX from "xlsx";
import * as pdfjsLib from "pdfjs-dist";
import mammoth from "mammoth";
import {
  CompareDASTInsightsAndRecs,
  run2DASTSecurityMetrics,
  singleRunDASTInsightsAndRecs,
  singleRunDASTSecurityMetrics,
} from "../../../services/webServices";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import { useNavigate } from "react-router-dom";
import SessionExpired from "../../popup-components/SessionExpiredPopup";
import {
  SecurityFlowDataContext,
  SecurityLoaderStateContext,
} from "../../main-pages/SecurityTestingPage";
// 👇 PDF.js worker setup for client-side
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// ✅ Simple reuse style:
const toggleStyle = {
  flex: 1,
  "&.Mui-selected": {
    backgroundColor: "#e8f4fd",
    border: "2px solid #90caf9",
    "&:hover": { backgroundColor: "#e8f4fd" },
  },
  "&:not(.Mui-selected)": {
    backgroundColor: "#ffffff",
    border: "2px solid #e0e0e0",
    "&:hover": { backgroundColor: "#f5f5f5" },
  },
};
const toggleTitle = {
  fontSize: "18px",
  fontWeight: 600,
  textTransform: "none",
  color: "#000000",
  mb: 1,
};
const toggleDesc = {
  textTransform: "none",
  fontSize: "16px",
  fontWeight: 400,
  color: "#000000",
  mb: 1,
};
const toggleHint = {
  textTransform: "none",
  fontSize: "16px",
  fontWeight: 400,
  color: "#414141",
};

const DASTInsightsInputPage = () => {
  const navigate = useNavigate();
  const fileInputRef1 = useRef(null); // Separate ref for Run1
  const fileInputRef2 = useRef(null); // Separate ref for Run2

  // Separate states for Run1 and Run2
  const [run1File, setRun1File] = useState(null);
  const [run1Text, setRun1Text] = useState("");
  const [run1ParsedJSON, setRun1ParsedJSON] = useState(null);

  const [run2File, setRun2File] = useState(null);
  const [run2Text, setRun2Text] = useState("");
  const [run2ParsedJSON, setRun2ParsedJSON] = useState(null);

  const [errorMessage, setErrorMessage] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [scanner, setScanner] = useState("Single Run Analysis");
  const { setLoadingState } = useContext(LoadingModalContext);
  const [sessionExpired, setSessionExpired] = useState(false);
  const { setInsightsPhase } = useContext(SecurityFlowDataContext);
  const { setSecurityLoaderState } = useContext(SecurityLoaderStateContext);

  // Handle Run1 file input click
  const handleRun1Click = () => {
    if (fileInputRef1.current) {
      fileInputRef1.current.click();
    }
  };

  // Handle Run2 file input click
  const handleRun2Click = () => {
    if (fileInputRef2.current) {
      fileInputRef2.current.click();
    }
  };

  // Remove Run1 file
  const handleRemoveRun1File = () => {
    setRun1File(null);
    setRun1Text("");
    setRun1ParsedJSON(null);
    if (fileInputRef1.current) {
      fileInputRef1.current.value = "";
    }
    // Update scanner type based on remaining files
    if (!run2File) {
      setScanner("Single Run Analysis");
    }
  };

  // Remove Run2 file
  const handleRemoveRun2File = () => {
    setRun2File(null);
    setRun2Text("");
    setRun2ParsedJSON(null);
    if (fileInputRef2.current) {
      fileInputRef2.current.value = "";
    }
    // Update scanner type based on remaining files
    setScanner(run1File ? "Single Run Analysis" : "Single Run Analysis");
  };

  // Strict .xlsx validator for DAST uploads
  const validateFileTypeXlsx = (file, runLabel) => {
    if (!file) return { isValid: false, error: "No file selected" }; // user canceled [2][4]
    if (!Number.isFinite(file.size) || file.size <= 0) {
      return { isValid: false, error: "File appears to be empty or corrupted" }; // zero-byte guard [2]
    }
    const name = typeof file.name === "string" ? file.name.toLowerCase() : "";
    // Accept only .xlsx by extension; MIME can be unreliable across browsers
    if (name.endsWith(".xlsx")) return { isValid: true }; // pass [2]
    const ext = name.includes(".") ? name.slice(name.lastIndexOf(".")) : "";
    return {
      isValid: false,
      error: ext
        ? `File type ${ext} is not supported for ${runLabel}. Only .xlsx files are allowed.`
        : `Unsupported file type for ${runLabel}. Only .xlsx files are supported.`,
    }; // clear error [2][3]
  };

  // Handle Run1 file upload
  const handleRun1FileChange = async () => {
    setOpenErrorPopup(false);
    setErrorMessage("");

    // Always read from the input ref to avoid event target confusion
    const el = fileInputRef1.current;
    const file = el?.files?.[0] || null;

    // If user canceled file picker, do not show “unsupported”
    if (!file) return; // graceful exit [2][4]

    // Validate .xlsx only
    const validation = validateFileTypeXlsx(file, "Run-1");
    if (!validation.isValid) {
      setErrorMessage(validation.error);
      setOpenErrorPopup(true);
      el.value = ""; // reset so selecting the same file triggers onChange again [2]
      return;
    }

    try {
      const parsedJson = await run1ReadExcelFileAsJSON(file);
      if (!parsedJson || parsedJson.length === 0) {
        throw new Error("No valid vulnerability data found in the Excel file");
      }
      const extractedText = JSON.stringify(parsedJson, null, 2);

      setRun1File(file);
      setRun1Text(extractedText);
      setRun1ParsedJSON(parsedJson);

      if (run2File) setScanner("Comparison Analysis");
    } catch (error) {
      setErrorMessage(
        "Error processing Run-1 file: " + (error?.message || "Unknown error")
      );
      setOpenErrorPopup(true);
      el.value = ""; // reset input after error so user can re-pick [2]
    }
  };

  // Handle Run2 file upload
  const handleRun2FileChange = async () => {
    setOpenErrorPopup(false);
    setErrorMessage("");

    const el = fileInputRef2.current;
    const file = el?.files?.[0] || null;

    if (!file) return; // user canceled [2][4]

    const validation = validateFileTypeXlsx(file, "Run-2");
    if (!validation.isValid) {
      setErrorMessage(validation.error);
      setOpenErrorPopup(true);
      el.value = "";
      return;
    }

    try {
      const parsedJson = await run2ReadExcelFileAsJSON(file);
      if (!parsedJson || parsedJson.length === 0) {
        throw new Error("No valid vulnerability data found in the Excel file");
      }
      const extractedText = JSON.stringify(parsedJson, null, 2);

      setRun2File(file);
      setRun2Text(extractedText);
      setRun2ParsedJSON(parsedJson);

      if (run1File) setScanner("Comparison Analysis");
    } catch (error) {
      setErrorMessage(
        "Error processing Run-2 file: " + (error?.message || "Unknown error")
      );
      setOpenErrorPopup(true);
      el.value = "";
    }
  };

  // Keep your existing file reading functions
  const run1ReadExcelFileAsJSON = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawData = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: "",
          blankrows: false,
        });

        const headers = rawData[0];
        const dataRows = rawData.slice(1);

        function convertExcelDate(excelSerial) {
          const excelEpoch = new Date(Date.UTC(1899, 11, 30));
          const converted = new Date(
            excelEpoch.getTime() + excelSerial * 86400 * 1000
          );
          return converted;
        }

        function formatToDDMMYYYYHHMM(date) {
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          // const hours = String(date.getHours()).padStart(2, "0");
          // const minutes = String(date.getMinutes()).padStart(2, "0");
          return `${day}-${month}-${year}`;
        }

        function parseDate(value) {
          if (!value) return null;

          if (typeof value === "number") {
            return formatToDDMMYYYYHHMM(convertExcelDate(value));
          }

          if (typeof value === "string") {
            const parts = value.split(/[-/ :]/);
            if (parts.length >= 3) {
              const [dd, mm, yyyy] = parts;
              return `${dd}-${mm}-${yyyy}`;
            }
          }

          return null;
        }

        const cvssFullNames = {
          AV: "Attack Vector",
          AC: "Attack Complexity",
          PR: "Privileges Required",
          UI: "User Interaction",
          Scope: "Scope",
          C: "Confidentiality Impact",
          I: "Integrity Impact",
          A: "Availability Impact",
          E: "Exploit Code Maturity",
          RL: "Remediation Level",
          RC: "Report Confidence",
          MAV: "Modified Attack Vector",
          MAC: "Modified Attack Complexity",
          MPR: "Modified Privileges Required",
          MUI: "Modified User Interaction",
          MS: "Modified Scope",
          MC: "Modified Confidentiality",
          MI: "Modified Integrity",
          MA: "Modified Availability",
          CR: "Confidentiality Requirement",
          IR: "Integrity Requirement",
          AR: "Availability Requirement",
        };

        const results = [];
        let currentVuln = null;

        for (const row of dataRows) {
          const [
            vulnName,
            description,
            instance,
            evidence,
            status,
            severity,
            remediation,
            identifiedDateRaw,
            AV,
            AC,
            PR,
            UI,
            Scope,
            C,
            I,
            A,
            E,
            RL,
            RC,
            MAV,
            MAC,
            MPR,
            MUI,
            MS,
            MC,
            MI,
            MA,
            CR,
            IR,
            AR,
          ] = row;

          const isNewVuln = vulnName && description;
          const identifiedDate = parseDate(identifiedDateRaw);

          const cvss = {
            "Attack Vector": AV,
            "Attack Complexity": AC,
            "Privileges Required": PR,
            "User Interaction": UI,
            Scope: Scope,
            "Confidentiality Impact": C,
            "Integrity Impact": I,
            "Availability Impact": A,
            "Exploit Code Maturity": E,
            "Remediation Level": RL,
            "Report Confidence": RC,
            "Modified Attack Vector": MAV,
            "Modified Attack Complexity": MAC,
            "Modified Privileges Required": MPR,
            "Modified User Interaction": MUI,
            "Modified Scope": MS,
            "Modified Confidentiality": MC,
            "Modified Integrity": MI,
            "Modified Availability": MA,
            "Confidentiality Requirement": CR,
            "Integrity Requirement": IR,
            "Availability Requirement": AR,
          };

          if (isNewVuln) {
            currentVuln = {
              name: vulnName,
              description,
              instances: [],
              status,
              severity,
              remediation,
              identifiedDate,
              cvss,
            };

            if (instance) {
              currentVuln.instances.push({ url: instance, evidence });
            }

            results.push(currentVuln);
          } else if (currentVuln && instance) {
            currentVuln.instances.push({ url: instance, evidence });
          }
        }

        resolve(results);
      };

      reader.onerror = (err) => reject(err);
      reader.readAsArrayBuffer(file);
    });
  };

  const run2ReadExcelFileAsJSON = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawData = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: "",
          blankrows: false,
        });

        // Skip header row
        const headers = rawData[0];
        const dataRows = rawData.slice(1);

        // Excel date conversion functions
        function convertExcelDate(excelSerial) {
          const excelEpoch = new Date(Date.UTC(1899, 11, 30));
          const converted = new Date(
            excelEpoch.getTime() + excelSerial * 86400 * 1000
          );
          return converted;
        }

        function formatToDDMMYYYYHHMM(date) {
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          return `${day}-${month}-${year}`;
        }

        function parseDate(value) {
          if (!value) return null;

          if (typeof value === "number") {
            return formatToDDMMYYYYHHMM(convertExcelDate(value));
          }

          if (typeof value === "string") {
            const parts = value.split(/[-/ :]/);
            if (parts.length >= 3) {
              const [dd, mm, yyyy] = parts;
              return `${dd}-${mm}-${yyyy}`;
            }
          }

          return null;
        }

        const results = [];
        let currentVuln = null;

        for (const row of dataRows) {
          // Map columns based on your new structure
          const [
            vulnName, // Vulnerability Name
            description, // Description
            instance, // Instances
            evidence, // Evidence
            status, // Vulnerability Status
            severity, // Severity
            remediation, // Remediation
            identifiedDateRaw, // Vulnerability Identified Date
            remediationStatus, // Vulnerability Remediation Status
            remediationDateRaw, // Vulnerability Remediation Date
          ] = row;

          const isNewVuln = vulnName && description;
          const identifiedDate = parseDate(identifiedDateRaw);
          const remediationDate = parseDate(remediationDateRaw);

          if (isNewVuln) {
            currentVuln = {
              name: vulnName,
              description,
              instances: [],
              status,
              severity,
              remediation,
              identifiedDate,
              remediationStatus,
              remediationDate,
            };

            // Add instance if it exists
            if (instance) {
              currentVuln.instances.push({
                url: instance,
                evidence: evidence || "",
              });
            }

            results.push(currentVuln);
          } else if (currentVuln && instance) {
            // Additional instances for the same vulnerability
            currentVuln.instances.push({
              url: instance,
              evidence: evidence || "",
            });
          }
        }

        console.log(
          `✅ Processed ${results.length} vulnerabilities from Excel`
        );
        resolve(results);
      };

      reader.onerror = (err) => {
        console.error("❌ Error reading Excel file:", err);
        reject(err);
      };

      reader.readAsArrayBuffer(file);
    });
  };

  const handleInitiateScan = async () => {
    // Check if we have the required files based on scanner type
    if (scanner === "Single Run Analysis" && !run1File) {
      setOpenErrorPopup(true);
      setErrorMessage(
        "Please upload Run1 DAST report for single run analysis."
      );
      return;
    }

    if (scanner === "Comparison Analysis" && (!run1File || !run2File)) {
      setOpenErrorPopup(true);
      setErrorMessage(
        "Please upload both Run1 and Run2 DAST reports for comparison analysis."
      );
      return;
    }

    let fake;
    let navigationBlocked = false;

    // Enhanced navigation blocking function
    const blockNavigation = () => {
      navigationBlocked = true;

      // 1. Block back/forward navigation
      const handlePopState = (event) => {
        if (navigationBlocked) {
          // Immediately push forward again to cancel the back navigation
          window.history.pushState(null, null, window.location.href);

          // Optional: Show user feedback
          alert("Please wait, scan is in progress...");
        }
      };

      // 2. Block page refresh and closing
      const handleBeforeUnload = (event) => {
        if (navigationBlocked) {
          event.preventDefault();
          event.returnValue =
            "Scan is in progress. Leaving will cancel the operation.";
          return event.returnValue;
        }
      };

      // 3. Block keyboard shortcuts (Ctrl+R, F5, etc.)
      const handleKeyDown = (event) => {
        if (navigationBlocked) {
          // Block F5, Ctrl+R, Ctrl+F5
          if (
            event.key === "F5" ||
            (event.ctrlKey && event.key === "r") ||
            (event.ctrlKey && event.key === "R")
          ) {
            event.preventDefault();
            alert("Please wait, scan is in progress...");
          }

          // Block Alt+Left (back) and Alt+Right (forward)
          if (
            event.altKey &&
            (event.key === "ArrowLeft" || event.key === "ArrowRight")
          ) {
            event.preventDefault();
            alert("Navigation is disabled during scan processing...");
          }
        }
      };

      // Add all event listeners
      window.addEventListener("popstate", handlePopState);
      window.addEventListener("beforeunload", handleBeforeUnload);
      window.addEventListener("keydown", handleKeyDown);

      // Push initial state to create navigation barrier
      window.history.pushState(null, null, window.location.href);

      // Return cleanup function
      return () => {
        navigationBlocked = false;
        window.removeEventListener("popstate", handlePopState);
        window.removeEventListener("beforeunload", handleBeforeUnload);
        window.removeEventListener("keydown", handleKeyDown);
      };
    };

    const startProgress = (setProgress) => {
      let progress = 0;
      let stop = false;
      let finalMessageShown = false;

      const interval = setInterval(() => {
        if (stop) return;

        progress += 0.5;

        if (progress >= 97 && !finalMessageShown) {
          finalMessageShown = true;
          setLoadingState((prev) => ({
            ...prev,
            message: "Almost done! Fetching your output",
          }));
        }

        if (progress >= 97) progress = 97;

        setProgress((prev) => ({
          ...prev,
          progress: Math.max(prev.progress, Math.floor(progress)),
        }));
      }, 200);

      return {
        stop: () => {
          stop = true;
          clearInterval(interval);
        },
        completeTo100: async () => {
          stop = true;
          clearInterval(interval);
          setProgress((prev) => ({
            ...prev,
            progress: 100,
          }));
          await new Promise((res) => setTimeout(res, 500));
        },
      };
    };

    setLoadingState({
      open: true,
      message: "Generating DAST Insights",
      progress: 0,
    });

    fake = startProgress(setLoadingState);

    // Start blocking navigation
    const unblockNavigation = blockNavigation();

    setSecurityLoaderState("Insights Scan");

    try {
      if (scanner === "Single Run Analysis") {
        // console.log("Run1 Parsed JSON:", run1ParsedJSON);
        // Call the API for single run analysis
        const securityMetricsResponse = await singleRunDASTSecurityMetrics({
          run: run1ParsedJSON,
        });
        // console.log("Single Run Response:", securityMetricsResponse);
        const insightsAndRecsResponse = await singleRunDASTInsightsAndRecs({
          run: securityMetricsResponse.data?.singleRunsecurityMetrics,
        });
        // console.log(
        //   "Insights and Recommendations Response:",
        //   insightsAndRecsResponse.data.insightsPerSection
        // );
        // Navigate to the dashboard with insights and recommendations
        setInsightsPhase({
          runAnalysis: "Single Run Analysis",
          securityMetrics:
            securityMetricsResponse.data?.singleRunsecurityMetrics,
          insightsAndRecs:
            insightsAndRecsResponse.data.insightsPerSection || [],
        });
        navigate("/dashboard/security-testing/dast-insights-dashboard", {
          state: {
            runAnalysis: "Single Run Analysis",
            securityMetrics:
              securityMetricsResponse.data?.singleRunsecurityMetrics,
            insightsAndRecs:
              insightsAndRecsResponse.data.insightsPerSection || [],
          },
        });
      } else if (scanner === "Comparison Analysis") {
        // console.log("Run1 Parsed JSON:", run1ParsedJSON);
        // console.log("Run2 Parsed JSON:", run2ParsedJSON);

        // Implement comparison analysis API call
        const securityMetricsResponse = await singleRunDASTSecurityMetrics({
          run: run1ParsedJSON,
        });

        const run1Metrics =
          securityMetricsResponse.data?.singleRunsecurityMetrics;
        // console.log("Single Run Response:", run1Metrics);

        const comparisonMetricsResponse = await run2DASTSecurityMetrics({
          run1: run1Metrics,
          run2: run2ParsedJSON,
        });
        const run2Metrics = comparisonMetricsResponse.data?.run2SecurityMetrics;
        // console.log("Comparison Run Response:", comparisonMetricsResponse.data);

        const inightsAndRecsResponse = await CompareDASTInsightsAndRecs({
          run1: run1Metrics,
          run2: run2Metrics,
        });
        // console.log("Insights Response:", inightsAndRecsResponse.data);
        setInsightsPhase({
          runAnalysis: "Comparison Analysis",
          securityMetrics: {
            run1Metrics: run1Metrics,
            run2Metrics: run2Metrics,
          },
          insightsAndRecs: inightsAndRecsResponse.data?.insightsPerSection,
        });
        navigate("/dashboard/security-testing/dast-insights-dashboard", {
          state: {
            runAnalysis: "Comparison Analysis",
            securityMetrics: {
              run1Metrics: run1Metrics,
              run2Metrics: run2Metrics,
            },
            insightsAndRecs: inightsAndRecsResponse.data?.insightsPerSection,
          },
        });
      }

      // Success: unblock navigation and stop progress
      unblockNavigation();
      fake?.stop();
      setLoadingState({ open: false, message: "", progress: null });
    } catch (error) {
      // Error: unblock navigation and stop progress
      unblockNavigation();
      fake?.stop();

      if (error.response?.data?.message === "Your session has expired.") {
        setSessionExpired(true);
        setOpenErrorPopup(false);
      }
      console.error("Error initiating scan:", error);
      setLoadingState({ open: false, message: "", progress: null });
      setOpenErrorPopup(true);
      setErrorMessage("Error processing the scan: " + error.message);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Box
        sx={{
          height: { xs: "auto", md: "15vh" },
          backgroundColor: "#FFFFFF",
          display: "flex",
          alignItems: "center",
          padding: 2,
          mt: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            width: "100%",
            gap: 2,
            p: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "27px",
                fontWeight: 700,
                color: "#000000",
              }}
            >
              DAST Metrics Analyzer
            </Typography>
            <Typography
              sx={{
                fontSize: "18px",
                color: "#000000",
                fontWeight: 400,
                mt: 2,
              }}
            >
              Scan Analysis & Intelligence Platform
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          padding: 2,
          backgroundColor: "#FFFFFF",
          minHeight: "85vh",
          m: 4,
          mt: 4,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "38px",
              color: "#000000",
              fontWeight: 600,
            }}
          >
            Analyze Your DAST Security Reports
          </Typography>
          <Typography
            sx={{
              fontSize: "18px",
              color: "#000000",
              fontWeight: 500,
              mt: 2,
            }}
          >
            Upload your Dynamic Application Security Testing (DAST) reports to
            get comprehensive security metrics and insights, and actionable
            recommendations for your application.
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          {/* Toggle Tabs */}
          <Box sx={{ display: "flex", gap: 2, mt: 4, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <ToggleButtonGroup
                exclusive
                value={scanner}
                onChange={(e, newScanner) => {
                  if (newScanner) {
                    setScanner(newScanner);
                  }
                }}
                fullWidth
                sx={{ height: "183px" }}
              >
                <ToggleButton value="Single Run Analysis" sx={toggleStyle}>
                  <Box>
                    <Typography sx={toggleTitle}>
                      Single Run Analysis
                    </Typography>
                    <Typography sx={toggleDesc}>
                      Analyze a single DAST scan run for comprehensive security
                      metrics
                    </Typography>
                    <Typography sx={toggleHint}>
                      Requires a single DAST report
                    </Typography>
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box sx={{ flex: 1 }}>
              <ToggleButtonGroup
                exclusive
                value={scanner}
                onChange={(e, newScanner) => {
                  if (newScanner) {
                    setScanner(newScanner);
                  }
                }}
                fullWidth
                sx={{ height: "183px" }}
              >
                <ToggleButton value="Comparison Analysis" sx={toggleStyle}>
                  <Box>
                    <Typography sx={toggleTitle}>
                      Comparison Analysis
                    </Typography>
                    <Typography sx={toggleDesc}>
                      Compare two DAST scan runs to analyze security
                      improvements and trends
                    </Typography>
                    <Typography sx={toggleHint}>
                      Requires 2 DAST reports
                    </Typography>
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* Run1 Upload Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: 4,
            }}
          >
            <Typography
              sx={{
                fontSize: "23px",
                color: "#0094FB",
                fontWeight: 600,
              }}
            >
              Upload Run-1 DAST Report
            </Typography>

            {!run1File ? (
              <Paper
                variant="outlined"
                sx={{
                  pt: 2,
                  pb: 2,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2px dashed #90caf9",
                  cursor: "pointer",
                  mb: 4,
                  mt: 2,
                  backgroundColor: "#EAF4FF",
                  width: "100%",
                  height: "119px",
                  position: "relative",
                  gap: 2,
                }}
                onClick={handleRun1Click}
              >
                <SecurityUploadIcon width={24} height={24} />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <Typography
                    variant="body1"
                    component="span"
                    sx={{
                      color: "black",
                      ":hover": { textDecoration: "underline" },
                    }}
                  >
                    Drop your Run-1 DAST report here or click to browse
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Supports XLSX formats. Upload a file
                  </Typography>
                </Box>
                <input
                  ref={fileInputRef1}
                  type="file"
                  accept=".xlsx"
                  hidden
                  onChange={handleRun1FileChange}
                />
              </Paper>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  mt: 2,
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    border: "2px solid #90caf9",
                    mb: 2,
                    backgroundColor: "#EAF4FF",
                    width: "100%",
                    position: "relative",
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <BusinessDocIcon width={24} height={24} />
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "black",
                        }}
                      >
                        {run1File.name}
                      </Typography>
                      <Typography sx={{ fontSize: "12px", color: "#00000080" }}>
                        {(run1File.size / 1024).toFixed(1)} KB
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    aria-label="delete"
                    onClick={handleRemoveRun1File}
                  >
                    <DeleteIcon sx={{ color: "#f44336" }} />
                  </IconButton>
                </Paper>
              </Box>
            )}
          </Box>

          {/* Run2 Upload Section - Only show if Comparison Analysis is selected */}
          {scanner === "Comparison Analysis" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mt: 4,
              }}
            >
              <Typography
                sx={{
                  fontSize: "23px",
                  color: "#0094FB",
                  fontWeight: 600,
                }}
              >
                Upload Run-2 DAST Report
              </Typography>

              {!run2File ? (
                <Paper
                  variant="outlined"
                  sx={{
                    pt: 2,
                    pb: 2,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed #90caf9",
                    cursor: "pointer",
                    mb: 4,
                    mt: 2,
                    backgroundColor: "#EAF4FF",
                    width: "100%",
                    height: "119px",
                    position: "relative",
                    gap: 2,
                  }}
                  onClick={handleRun2Click}
                >
                  <SecurityUploadIcon width={24} height={24} />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <Typography
                      variant="body1"
                      component="span"
                      sx={{
                        color: "black",
                        ":hover": { textDecoration: "underline" },
                      }}
                    >
                      Drop your Run-2 DAST report here or click to browse
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Supports XLSX formats. Upload a file
                    </Typography>
                  </Box>
                  <input
                    ref={fileInputRef2}
                    type="file"
                    accept=".xlsx"
                    hidden
                    onChange={handleRun2FileChange}
                  />
                </Paper>
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    mt: 2,
                  }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      border: "2px solid #90caf9",
                      mb: 2,
                      backgroundColor: "#EAF4FF",
                      width: "100%",
                      position: "relative",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <BusinessDocIcon width={24} height={24} />
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "black",
                          }}
                        >
                          {run2File.name}
                        </Typography>
                        <Typography
                          sx={{ fontSize: "12px", color: "#00000080" }}
                        >
                          {(run2File.size / 1024).toFixed(1)} KB
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      aria-label="delete"
                      onClick={handleRemoveRun2File}
                    >
                      <DeleteIcon sx={{ color: "#f44336" }} />
                    </IconButton>
                  </Paper>
                </Box>
              )}
            </Box>
          )}

          {/* Action Button */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Button
              disabled={
                (scanner === "Single Run Analysis" && !run1File) ||
                (scanner === "Comparison Analysis" && (!run1File || !run2File))
              }
              sx={{
                color: "#FFFFFF",
                backgroundColor: "#4DBD74",
                textTransform: "none",
                width: "256px",
                height: "50px",
                borderRadius: "8px",
                "&:disabled": {
                  backgroundColor: "#cccccc",
                  color: "#666666",
                },
              }}
              onClick={handleInitiateScan}
            >
              {scanner === "Single Run Analysis"
                ? "Start Single Run Analysis"
                : "Start Comparison Analysis"}
            </Button>
          </Box>
        </Box>
      </Box>

      {openErrorPopup && (
        <MessagePopup
          modalOpen={openErrorPopup}
          okayHandler={() => setOpenErrorPopup(false)}
          message={errorMessage}
        />
      )}

      {sessionExpired && <SessionExpired />}
    </Box>
  );
};

export default DASTInsightsInputPage;
